<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import TicketFormsAPI from 'dashboard/api/ticketForms';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

// Manages the ordered formularios of a tramite type. The admission row (role: admission) is pinned
// first, audience-locked to citizen; only its bound form is swappable. Other rows are stage-attached
// or ad-hoc, each with an audience and a required flag. Persisted individually against the nested
// forms sub-resource, mirroring StagesEditor.
const props = defineProps({
  typeId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();

const forms = ref([]);
const stages = ref([]);
const availableForms = ref([]);
const loading = ref(true);
const savingKey = ref(null);
let tmpCounter = 0;

const sorted = computed(() =>
  [...forms.value].sort((a, b) => {
    if (a.role === 'admission') return -1;
    if (b.role === 'admission') return 1;
    return (a.position ?? 0) - (b.position ?? 0);
  })
);

function rowKey(form) {
  return form.id ? `id-${form.id}` : `tmp-${form._key}`;
}

onMounted(async () => {
  try {
    const [formData, stageData] = await Promise.all([
      store.dispatch('tramiteTypes/fetchForms', props.typeId),
      store.dispatch('tramiteTypes/fetchStages', props.typeId),
      TicketFormsAPI.get().then(res => {
        availableForms.value = res.data || [];
      }),
    ]);
    forms.value = formData || [];
    stages.value = stageData || [];
  } catch {
    useAlert(t('TRAMITE_TYPES.FORMS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
});

function addForm() {
  tmpCounter += 1;
  forms.value = [
    ...forms.value,
    {
      _key: tmpCounter,
      ticket_form_id: null,
      role: 'stage',
      stage_key: '',
      audience: 'citizen',
      required: false,
      position: forms.value.length,
    },
  ];
}

function buildPayload(form) {
  const { id, _key, key, tramite_type_id, created_at, updated_at, ...attrs } = form;
  return attrs;
}

async function saveForm(form) {
  savingKey.value = rowKey(form);
  try {
    const payload = buildPayload(form);
    const saved = form.id
      ? await store.dispatch('tramiteTypes/updateForm', { typeId: props.typeId, formId: form.id, ...payload })
      : await store.dispatch('tramiteTypes/createForm', { typeId: props.typeId, ...payload });
    const idx = forms.value.findIndex(f => (f.id ? f.id === form.id : f._key === form._key));
    if (idx !== -1) forms.value.splice(idx, 1, saved);
    useAlert(t('TRAMITE_TYPES.FORMS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('TRAMITE_TYPES.FORMS.SAVE_ERROR'));
  } finally {
    savingKey.value = null;
  }
}

async function deleteForm(form) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TRAMITE_TYPES.FORMS.DELETE_CONFIRM', { name: form.key || '' }))) return;
  if (form.id) await store.dispatch('tramiteTypes/deleteForm', { typeId: props.typeId, formId: form.id });
  const idx = forms.value.findIndex(f => (f.id ? f.id === form.id : f._key === form._key));
  if (idx !== -1) forms.value.splice(idx, 1);
}
</script>

<template>
  <div class="flex flex-col gap-3 max-w-2xl">
    <div class="flex justify-between items-center">
      <h2 class="text-sm font-medium text-n-slate-12">{{ t('TRAMITE_TYPES.FORMS.TITLE') }}</h2>
      <Button size="sm" icon="i-lucide-plus" :label="t('TRAMITE_TYPES.FORMS.ADD')" @click="addForm" />
    </div>
    <p class="text-xs text-n-slate-10">{{ t('TRAMITE_TYPES.FORMS.HELP') }}</p>

    <div v-if="loading" class="flex justify-center py-8"><Spinner /></div>

    <div v-else class="flex flex-col gap-2">
      <p v-if="!sorted.length" class="py-6 text-sm text-center text-n-slate-10">
        {{ t('TRAMITE_TYPES.FORMS.EMPTY') }}
      </p>

      <div
        v-for="(form, index) in sorted"
        :key="rowKey(form)"
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak"
      >
        <div class="flex gap-2 items-center">
          <span
            v-if="form.role === 'admission'"
            class="px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('TRAMITE_TYPES.FORMS.ADMISSION_BADGE') }}
          </span>
          <select
            v-model="form.ticket_form_id"
            class="flex-1 px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option :value="null">{{ t('TRAMITE_TYPES.FORMS.NONE') }}</option>
            <option v-for="f in availableForms" :key="f.id" :value="f.id">{{ f.name }}</option>
          </select>
        </div>

        <div v-if="form.role !== 'admission'" class="flex flex-wrap gap-3">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('TRAMITE_TYPES.FORMS.ROLE_LABEL') }}
            <select v-model="form.role" class="px-2 py-1 text-sm rounded border border-n-weak bg-n-alpha-black2">
              <option value="stage">{{ t('TRAMITE_TYPES.FORMS.ROLE_STAGE') }}</option>
              <option value="adhoc">{{ t('TRAMITE_TYPES.FORMS.ROLE_ADHOC') }}</option>
            </select>
          </label>
          <label v-if="form.role === 'stage'" class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('TRAMITE_TYPES.FORMS.STAGE_LABEL') }}
            <select v-model="form.stage_key" class="px-2 py-1 text-sm rounded border border-n-weak bg-n-alpha-black2">
              <option v-for="s in stages" :key="s.stage_key" :value="s.stage_key">{{ s.name }}</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('TRAMITE_TYPES.FORMS.AUDIENCE_LABEL') }}
            <select v-model="form.audience" class="px-2 py-1 text-sm rounded border border-n-weak bg-n-alpha-black2">
              <option value="citizen">{{ t('TRAMITE_TYPES.FORMS.AUDIENCE_CITIZEN') }}</option>
              <option value="internal">{{ t('TRAMITE_TYPES.FORMS.AUDIENCE_INTERNAL') }}</option>
            </select>
          </label>
          <label class="flex gap-2 items-center self-end text-xs text-n-slate-12">
            <input v-model="form.required" type="checkbox" class="rounded" />
            {{ t('TRAMITE_TYPES.FORMS.REQUIRED_LABEL') }}
          </label>
        </div>

        <div class="flex gap-2 justify-end">
          <Button
            v-if="form.role !== 'admission'"
            variant="ghost"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            @click="deleteForm(form)"
          />
          <Button
            size="sm"
            :is-loading="savingKey === rowKey(form)"
            :label="t('TRAMITE_TYPES.FORMS.SAVE')"
            @click="saveForm(form)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
